import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Alert, Box, Button, Chip, Paper, Typography } from '@mui/material';

import type { PlaybookReadiness } from '../../services/playbookPersonaApi';

interface ReadinessGateProps {
  readiness: PlaybookReadiness;
}

/**
 * What has to be in place before a playbook track can be built.
 *
 * Shown instead of the authoring step, not alongside it. The AI customer is
 * synthesised from what the company sells; with that missing the model invents
 * a company and the salesperson ends up practising answers that are wrong for
 * the real product — worse than no training at all. Better to say so before
 * someone spends five minutes describing a customer.
 */
export const ReadinessGate: React.FC<ReadinessGateProps> = ({ readiness }) => {
  const navigate = useNavigate();

  return (
    <Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {readiness.blockers.length === 1
          ? 'One thing has to be in place before this track can be built. It is one-off setup. '
            + 'Once done, every playbook track you create uses it.'
          : 'Two things have to be in place before this track can be built. Both are one-off '
            + 'setup. Once done, every playbook track you create uses them.'}
      </Typography>

      {readiness.blockers.map((blocker) => (
        <Paper key={blocker.code} variant="outlined" sx={{ p: 2.5, mb: 2 }}>
          <Typography variant="subtitle1" sx={{ mb: 0.5 }}>{blocker.title}</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            {blocker.message}
          </Typography>

          {blocker.missing.length > 0 && (
            <Box sx={{ mb: 1.5 }}>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.75 }}>
                Still needed ({readiness.contextFieldsComplete} of {readiness.contextFieldsTotal} done):
              </Typography>
              {blocker.missing.map((field) => (
                <Box key={field.key} sx={{ mb: 0.5 }}>
                  <Typography variant="body2">
                    <strong>{field.label}</strong>
                    <Typography component="span" variant="body2" color="text.secondary">
                      {': '}{field.why}
                    </Typography>
                  </Typography>
                </Box>
              ))}
            </Box>
          )}

          <Button variant="contained" size="small" onClick={() => navigate(blocker.action.path)}>
            {blocker.action.label}
          </Button>
        </Paper>
      ))}

      {readiness.recommended.length > 0 && (
        <Alert severity="info" sx={{ mt: 1 }}>
          <Typography variant="body2">
            Not required, but the customer can push harder with it:{' '}
            {readiness.recommended.map((field) => (
              <Chip key={field.key} size="small" variant="outlined" label={field.label} sx={{ mr: 0.5 }} />
            ))}
          </Typography>
        </Alert>
      )}
    </Box>
  );
};

export default ReadinessGate;
